import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Upload, AlertCircle, CheckCircle, DollarSign, TrendingUp, Download, MessageSquare, Star, Type } from 'lucide-react';

const FIELDS = [
  { name: 'name',      label: 'App Name',     type: 'text',   icon: Type,          placeholder: 'e.g. WhatsApp' },
  { name: 'amount',    label: 'Amount (USDT)', type: 'number', icon: DollarSign,    placeholder: '100.00' },
  { name: 'profit',    label: 'Profit (USDT)', type: 'number', icon: TrendingUp,    placeholder: '5.00' },
  { name: 'downloads', label: 'Downloads',     type: 'text',   icon: Download,      placeholder: '1B+' },
  { name: 'reviews',   label: 'Reviews',       type: 'number', icon: MessageSquare, placeholder: '5000000' },
  { name: 'rating',    label: 'Rating',        type: 'text',   icon: Star,          placeholder: '4.5' },
];

const INPUT_CLS = 'w-full pl-9 pr-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500/30 transition-all';

const AddApp = () => {
  const [formData, setFormData] = useState({ name:'', amount:'', profit:'', downloads:'', reviews:'', rating:'', appImage: null });
  const [imagePreview, setImagePreview] = useState(null);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { token } = useAuth();

  const handleChange = (e) => {
    setFormData(p => ({ ...p, [e.target.name]: e.target.value }));
    setMessage(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(p => ({ ...p, appImage: file }));
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setFormData(p => ({ ...p, appImage: null }));
      setImagePreview(null);
    }
    setMessage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setIsSubmitting(true);

    if (!formData.name || !formData.amount || !formData.profit || !formData.appImage) {
      setMessage('Please fill in all required fields and upload an image.');
      setMessageType('error');
      setIsSubmitting(false);
      return;
    }

    const data = new FormData();
    ['name','amount','profit','downloads','reviews','rating'].forEach(k => data.append(k, formData[k]));
    data.append('appImage', formData.appImage);

    try {
      const res = await fetch('/api/apps', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: data,
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.msg || 'Failed to add app');

      setMessage('App added successfully!');
      setMessageType('success');
      setFormData({ name:'', amount:'', profit:'', downloads:'', reviews:'', rating:'', appImage: null });
      setImagePreview(null);
    } catch (err) {
      setMessage(err.message);
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white tracking-tight">Add New App</h1>
        <p className="text-sm text-gray-500 mt-1">Create a new app for users to rate</p>
      </div>

      <div className="max-w-2xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Image upload */}
          <div className="flex items-start gap-5 p-5 rounded-2xl bg-white/3 border border-white/8">
            <div className="relative shrink-0">
              <div className="w-20 h-20 rounded-2xl bg-white/5 border border-white/10 overflow-hidden flex items-center justify-center">
                {imagePreview
                  ? <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  : <Upload size={24} className="text-gray-600" />
                }
              </div>
              {imagePreview && (
                <button
                  type="button"
                  onClick={() => { setImagePreview(null); setFormData(p => ({...p, appImage: null})); }}
                  className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500/80 text-white flex items-center justify-center text-xs hover:bg-red-500 transition-all"
                >×</button>
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-white mb-1">App Icon</p>
              <p className="text-xs text-gray-500 mb-3">PNG, JPG or WebP · Max 5MB</p>
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-gray-300 hover:bg-white/8 hover:text-white transition-all">
                <Upload size={14} />
                {imagePreview ? 'Change Image' : 'Upload Image'}
                <input type="file" accept="image/*" onChange={handleFileChange} className="sr-only" />
              </label>
            </div>
          </div>

          {/* Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FIELDS.map(field => {
              const Icon = field.icon;
              return (
                <div key={field.name} className={field.name === 'name' ? 'sm:col-span-2' : ''}>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                    {field.label}
                    {['name','amount','profit'].includes(field.name) && <span className="text-red-400 ml-0.5">*</span>}
                  </label>
                  <div className="relative">
                    <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" />
                    <input
                      type={field.type}
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      placeholder={field.placeholder}
                      step={field.type === 'number' ? '0.01' : undefined}
                      className={INPUT_CLS}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Message */}
          {message && (
            <div className={`flex items-center gap-2 p-3.5 rounded-xl text-sm border ${
              messageType === 'success'
                ? 'bg-green-500/10 border-green-500/20 text-green-400'
                : 'bg-red-500/10 border-red-500/20 text-red-400'
            }`}>
              {messageType === 'success' ? <CheckCircle size={15} /> : <AlertCircle size={15} />}
              {message}
            </div>
          )}

          {/* Submit */}
          <div className="flex items-center gap-4 pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 px-8 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm font-bold shadow-lg shadow-cyan-500/20 hover:opacity-90 disabled:opacity-50 transition-all"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>
                  Adding…
                </>
              ) : 'Add App'}
            </button>
            <button
              type="button"
              onClick={() => { setFormData({ name:'', amount:'', profit:'', downloads:'', reviews:'', rating:'', appImage: null }); setImagePreview(null); setMessage(null); }}
              className="px-6 py-3 rounded-xl bg-white/5 border border-white/10 text-gray-400 text-sm font-semibold hover:text-white hover:bg-white/8 transition-all"
            >
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddApp;
